/**
 * Snooze and Dismiss for an asked question (ADR 0056, Dismiss and snooze), beside Answer on the
 * Inbox detail and the drawer card, and the line that replaces them once it is set aside:
 * "Dismissed by you, 16:40" with Undo. Only for asked questions: a blocked ask is handed back or
 * denied instead. Reversible, so it runs at once and offers Undo (content.md); errors are inline.
 */
import { AlarmClock, BellOff, ChevronDown, CornerUpLeft, Undo2 } from '@orgus/ui/icons';
import { useState } from 'react';
import type { AskSetAside, NeedsYouItem } from '@orgus/protocol';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, cn, formatClock, toUiError, toast } from '@orgus/ui';
import { useMe } from '../auth/useMe.ts';
import { useOrgus } from '../../lib/store.ts';
import { SNOOZE_LABEL, dismissAsk, handBackApproval, snoozeAsk, snoozeUntil, undoAsk } from './api.ts';
import type { SnoozeChoice } from './api.ts';

type Ask = Pick<NeedsYouItem, 'askId' | 'sessionId' | 'reason'>;

function errorText(e: unknown): string {
  const err = toUiError(e);
  return `${err.what} ${err.why ?? ''} ${err.next ?? ''}`.replace(/\s+/g, ' ').trim();
}

/** "you" for the signed-in person, else their id; the demo's owner is you. */
function useWho(): (by: string) => string {
  const me = useMe((s) => s.me?.actor.id ?? null);
  const mock = useOrgus((s) => s.mode === 'mock');
  return (by) => (by === me || ((mock || !me) && by === 'owner') ? 'you' : by);
}

/** What a set-aside reads: "Dismissed by you, 16:40", "Snoozed until 09:00", "Snoozed until the session moves". */
export function setAsideText(x: Pick<AskSetAside, 'kind' | 'until' | 'by' | 'at'>, who: (by: string) => string): string {
  if (x.kind === 'dismiss') return `Dismissed by ${who(x.by)}, ${formatClock(x.at)}`;
  return x.until == null ? 'Snoozed until the session moves' : `Snoozed until ${formatClock(x.until)}`;
}

function ErrorText({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">
      {text}
    </p>
  );
}

/** Snooze (a menu of three) and Dismiss, for an asked question. */
export function AskActions({ ask, className, align = 'end' }: { ask: Ask; className?: string; align?: 'start' | 'end' }) {
  const [busy, setBusy] = useState<'snooze' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const run = async (kind: 'snooze' | 'dismiss', work: () => Promise<unknown>, done: string) => {
    setBusy(kind);
    setError(null);
    try {
      await work();
      toast(done, { action: { label: 'Undo', onClick: () => void undoAsk(ask.askId).catch((e) => setError(errorText(e))) } });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  const snooze = (c: SnoozeChoice) => run('snooze', () => snoozeAsk(ask, snoozeUntil(c, Date.now())), 'Question snoozed');
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className={cn('flex flex-wrap items-center gap-2', align === 'end' && 'justify-end')}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" icon={<AlarmClock />} loading={busy === 'snooze'}>
              Snooze
              <ChevronDown className="size-4 text-fg-subtle" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={align} className="w-56">
            {(Object.keys(SNOOZE_LABEL) as SnoozeChoice[]).map((c) => (
              <DropdownMenuItem key={c} onSelect={() => void snooze(c)}>
                {SNOOZE_LABEL[c]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button size="sm" variant="ghost" icon={<BellOff />} loading={busy === 'dismiss'} onClick={() => void run('dismiss', () => dismissAsk(ask), 'Question dismissed')}>
          Dismiss
        </Button>
      </div>
      <ErrorText text={error} />
    </div>
  );
}

/** The line a set-aside question keeps, with Undo: the question stays, the record says who and when. */
export function SetAsideLine({ record, className }: { record: AskSetAside; className?: string }) {
  const who = useWho();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        <BellOff className="size-4 shrink-0 text-fg-subtle" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-caption text-fg-muted num">{setAsideText(record, who)}</span>
        <Button
          size="sm"
          variant="ghost"
          icon={<Undo2 />}
          loading={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await undoAsk(record.askId);
            } catch (e) {
              setError(errorText(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Undo
        </Button>
      </div>
      <ErrorText text={error} />
    </div>
  );
}

export { useWho };

/**
 * A held request of a session Orgus controls, handed back so Claude Code's own dialog in its
 * terminal decides (the blocked form of Dismiss, ADR 0056). Never offered in bulk.
 */
export function HandBackButton({ approvalId, className }: { approvalId: string; className?: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div>
        <Button
          size="sm"
          variant="ghost"
          icon={<CornerUpLeft />}
          loading={busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await handBackApproval(approvalId);
            } catch (e) {
              setError(errorText(e));
            } finally {
              setBusy(false);
            }
          }}
        >
          Hand back to the session
        </Button>
      </div>
      <ErrorText text={error} />
    </div>
  );
}
